import axios from './axios.setup.js';
import { accountDelete, accountGet, accountPatch, accountPost, accountPut } from './prefixedUrls.js';
import * as MsgTypes from './message.service.js';
import * as CompanyTypes from './company.service.js';
import { download } from '@/utils.js';

/**
 * @typedef Account
 * @property {boolean} active
 * @property {string} address
 * @property {string} alias
 * @property {boolean} auto_read
 * @property {number} company_id
 * @property {string} created_at
 * @property {string | null} deleted_at
 * @property {string} folder_inbox
 * @property {string} folder_outbox
 * @property {number} id
 * @property {string} imap_flags
 * @property {string} imap_hostname
 * @property {string} imap_port
 * @property {boolean} import_contacts
 * @property {number | null} legacy_id
 * @property {boolean} notification
 * @property {any} notification_recipients
 * @property {boolean} outbox_polling
 * @property {string} service
 * @property {string} smtp_flags
 * @property {string} smtp_hostname
 * @property {string} smtp_port
 * @property {string} type
 * @property {string} updated_at
 * @property {string} username
 * @property {string} uuid
 * @property {number | null} webmail_total_space
 * @property {number | null} webmail_used_space
 * @property {boolean} cleanup_active
 * @property {number} cleanup_quota_limit
 * @property {number | null} cleanup_months_limit
 * @property {number | null} cleanup_days_limit
 * @property {boolean} protocollable
 * @property {string} signature
 */

/**
 * @typedef AccountCompanyData
 * @property {CompanyTypes.Company} company
 * @property {Account[]} accounts
 */

/**
 * @typedef AccountStats
 * Le statistiche di un account. Ogni categoria contiene delle sezioni,
 * che contengono i dati divisi per anno
 * @property {Object} ricevute
 * @property {Object<string, Object<string, number>>} ricevute.tag
 * @property {Object<string, Object<string, number>>} ricevute.assegnazioni
 * @property {Object<string, Object<string, number>>} ricevute.lettura
 * @property {Object} inviate
 * @property {Object<string, Object<string, number>>} inviate.tag
 * @property {Object<string, Object<string, number>>} inviate.assegnazioni
 * @property {Object} notifiche
 * @property {Object<string, Object<string, number>>} notifiche.tag
 */


/**
 * @typedef ImapFolder
 * @property {number} id
 * @property {number} account_id
 * @property {string} name
 * @property {boolean} active
 * @property {string} created_at
 * @property {string} updated_at
 */

/**
 * Get all accounts
 * @param {{mini: boolean}} options
 * @returns {Promise<Account[]>}
 */
export async function getAll(options) {
  let url = '/accounts';

  if (options?.mini) {
    url += '?mini=1';
  }

  let res = await axios.get(url);
  return res.data;
}

/**
 * Get an account from the uuid
 * @param {string} id The account uuid
 * @returns {Promise<Account>}
 */
export async function get(id) {
  let res = await axios.get(`/accounts/${id}`)
  return res.data;
}

/**
 * Get the possible email statuses for a specific account
 * @param {string} id The account uuid
 * @returns {Promise<MsgTypes.MessageStatus[]>}
 */
export async function getStatuses(id) {
  let res = await accountGet('statuses', id);
  return res.data
}

/**
 * Creates a new account
 * @param {Account} account
 */
export async function create(account) {
  let res = await axios.post('/accounts', account);
  return res.data;
}

/**
 * Update an existing account
 * @param {Account} account
 */
export async function update(account) {
  let res = await accountPut('', account.uuid, account);
  return res.data;
}

/**
 * Changes the account password
 *
 * @param {string} id The account uuid
 * @param {string} password The new password
 */
export async function changePassword(id, password) {
  let res = await accountPatch('change-password', id, { password });
  return res.data;
}

/**
 * Sets the users that will have access to this account.
 *
 * @param {string} id The account uuid
 * @param {number[]} users The ids of the users
 */
export async function setAttachedUsers(id, users) {
  let res = await accountPatch('users', id, { users });
  return res.data;
}

/**
 * Return the company related to the account and all
 * the accounts related to the company.
 *
 * @param {string} accountId The account uuid
 * @returns {Promise<AccountCompanyData>}
 */
export async function companyData(accountId) {
  let res = await accountGet('company', accountId);
  return res.data;
}

/**
 * Get the number of unseen messages for this account
 * @param {string} accountId The account uuid
 * @returns {Promise<number>}
 */
export async function getUnseen(accountId) {
  let res = await accountGet('messages/unseen', accountId);
  return res.data;
}

/**
 * @typedef ConnectionTestResponse
 * @property {string[]} errors
 * @property {boolean} oauth
 * @property {boolean} imap
 * @property {boolean} smtp
 */

/**
 * Checks the account IMAP connection
 * @param {string} id The account uuid
 * @param {string} password A password to use instead of the one set in the Account
 * @returns {Promise<ConnectionTestResponse>}
 */
export async function testConnection(id, password) {
  let res = await accountGet('connection', id, { password });
  return res.data;
}

/**
 * Get the account stats
 * @param {string} id The account uuid
 * @returns {Promise<{account: Account, stats: AccountStats}>}
 */
export async function getStats(id) {
  let res = await accountGet('stats', id);
  return res.data;
}

/**
 * Get the account folders (IMAP)
 * @param {string} id The account uuid
 * @returns {Promise<Record<string, { IMAP: number, DB: number }>>}
 */
export async function getImapFolders(id) {
  let res = await accountGet('imap-folders', id);
  return res.data;
}

/**
 * Get the account folders (IMAP) that are currently
 * being syncronized.
 * @param {string} id The account uuid
 * @returns {Promise<ImapFolder[]>}
 */
export async function getActiveFolders(id) {
  let res = await accountGet('active-folders', id);
  return res.data;
}

/**
 * Sets the account folders (IMAP) that are currently
 * being syncronized.
 * @param {string} id The account uuid
 * @param {Array<{name: string, active: boolean}>} folders The folder to syncronize
 * @returns {Promise<ImapFolder[]>}
 */
export async function setActiveFolders(id, folders) {
  let res = await accountPut('set-active-folders', id, { folders });
  return res.data;
}

/**
 * Refreshes the quota data for an account
 * @param {string} id The account uuid
 * @returns {Promise<Account>}
 */
export async function refreshQuota(id) {
  let res = await accountPost('quota', id);
  return res.data;
}

/**
 * Executes a cleanup of the account following its specification (quota/months limit)
 * @param {string} id The account uuid
 * @param {string} folder The imap folder to clean
 * @param {boolean} force Whether to force the imap cleaning even when the quota data is not available
 * @returns {Promise<{report: string}>}
 */
export async function cleanup(id, folder, force = false) {
  let res = await accountPost('cleanup', id, { folder, force });
  return res.data;
}

/**
 * Downloads the report that has been generated after an account cleanup
 * @param {string} id The account uuid
 * @param {string} reportName The name of the report
 * @returns {Promise<void>}
 */
export async function downloadCleanupReport(id, reportName) {
  let res = await axios({
    url: `/accounts/${id}/download-cleanup-report/${reportName}`,
    method: 'GET',
    responseType: 'blob'
  });

  let blob = new Blob([res.data]);
  download(blob, reportName);
}

/**
 * Removes an account
 * @param {string} id The account uuid
 */
export async function remove(id) {
  let res = await accountDelete('', id);
  return res.data;
}
